// Recent list preference — a recent list's view (pins, removed from Recent,
// open order, sort, density), persisted per device.
//
// The design says per person; there is no server-side preference store yet,
// so like the rail, the dock and the library sort this is DEVICE display
// chrome (memory project_theme_outside_event_log): its own localStorage key
// per list, never a domain event. Only the host reads/writes; the parse /
// serialize pair and the fold are pure (recentList.ts).
//
// One store per list, so every surface showing a list (the left menu, the
// Projects page) sees the same pins at once: it is read from the device once,
// advanced only through `foldRecentList`, written back on every change, and
// each change notifies the surfaces subscribed to that list.

export { recentListStore };

import {
    foldRecentList,
    parseRecentList,
    type RecentListCommand,
    type RecentListView,
    serializeRecentList,
} from "../components/Sidebar/recentList.js";
import { readDeviceKey, writeDeviceKey } from "./deviceStore.js";

type RecentListStore = {
    readonly get: () => RecentListView;
    readonly subscribe: (onChange: () => void) => () => void;
    readonly dispatch: (cmd: RecentListCommand) => void;
};

// One key per list, so each list keeps its own pins and view.
const keyOf = (list: string): string => `bilda.recentList.${list}`;

const stores = new Map<string, RecentListStore>();

const mkStore = (
    list: string,
    defaults: RecentListView | undefined,
): RecentListStore => {
    let view = parseRecentList(readDeviceKey(keyOf(list)), defaults);
    const listeners = new Set<() => void>();
    return {
        get: () => view,
        subscribe: (onChange) => {
            listeners.add(onChange);
            return () => listeners.delete(onChange);
        },
        dispatch: (cmd) => {
            const next = foldRecentList(view, cmd);
            if (next === view) return;
            view = next;
            writeDeviceKey(keyOf(list), serializeRecentList(view));
            for (const onChange of listeners) onChange();
        },
    };
};

// `defaults`: what a list nothing was stored for starts as (read once, when
// the list is first used).
const recentListStore = (
    list: string,
    defaults?: RecentListView,
): RecentListStore => {
    const known = stores.get(list);
    if (known !== undefined) return known;
    const store = mkStore(list, defaults);
    stores.set(list, store);
    return store;
};
